import { toNestErrors, validateFieldsNatively } from "@hookform/resolvers";
import {
  appendErrors,
  type FieldError,
  type FieldErrors,
  type FieldValues,
  type Resolver,
} from "react-hook-form";
import { z } from "zod";

function parseIssues(issues: z.ZodIssue[], validateAllFieldCriteria: boolean) {
  const errors: Record<string, FieldError> = {};

  for (const issue of issues) {
    const path = issue.path.length > 0 ? issue.path.join(".") : "root";

    if (!errors[path]) {
      errors[path] = {
        message: issue.message,
        type: issue.code,
      };
    }

    if (validateAllFieldCriteria) {
      const types = errors[path].types;
      const messages = types?.[issue.code];

      errors[path] = appendErrors(
        path,
        validateAllFieldCriteria,
        errors,
        issue.code,
        messages ? ([] as string[]).concat(messages as string[], issue.message) : issue.message,
      ) as FieldError;
    }
  }

  return errors;
}

// react-hook-form's installed zod resolver still expects Zod v3's `error.errors`.
// This compat wrapper uses `safeParseAsync` so Zod v4 validation returns field errors
// instead of throwing and leaving submit state stuck.
export function zodResolverCompat<TFieldValues extends FieldValues>(
  schema: z.ZodTypeAny,
): Resolver<TFieldValues> {
  return async (values, _, options) => {
    const result = await schema.safeParseAsync(values);

    if (result.success) {
      if (options.shouldUseNativeValidation) {
        validateFieldsNatively({}, options);
      }

      return {
        errors: {},
        values: result.data as TFieldValues,
      };
    }

    return {
      values: {},
      errors: toNestErrors(
        parseIssues(
          [...result.error.issues],
          !options.shouldUseNativeValidation && options.criteriaMode === "all",
        ),
        options,
      ) as FieldErrors<TFieldValues>,
    };
  };
}
